import React from 'react';
import { ArrowRight, Phone, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { BBBSeal } from '../components/BBBSeal';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { stats } from '../mock';

const Home = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section with Large Background Image */}
      <section className="relative h-screen min-h-[700px] flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1733431774078-692252a6605e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxkcnl3YWxsJTIwY29uc3RydWN0aW9ufGVufDB8fHx8MTc3NDQ4Mjc2Mnww&ixlib=rb-4.1.0&q=85"
            alt="Professional Drywall Construction"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/75 to-gray-900/60"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-4">
          <div className="max-w-4xl">
            {/* Logo */}
            <div className="mb-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
              <div className="inline-block bg-white/95 backdrop-blur-sm px-6 py-4 rounded-lg shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_wall-works-1/artifacts/t9oeun2w_IMG_5700.jpeg" 
                  alt="Sat Drywall Ltd" 
                  className="h-28 md:h-36 w-auto"
                />
              </div>
            </div>

            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight animate-in fade-in slide-in-from-bottom-4 duration-700 delay-100">
              Professional Drywall & <br/>Insulation Services
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed max-w-2xl animate-in fade-in slide-in-from-bottom-4 duration-700 delay-200">
              Expert solutions for commercial and residential projects with unmatched quality and reliability
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-12 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-300">
              <Button 
                onClick={() => navigate('/contact')}
                className="bg-[#00AEEF] hover:bg-[#0096d1] text-white px-8 py-6 text-lg font-semibold rounded-lg transition-all duration-300 hover:shadow-xl hover:scale-105"
              >
                Get Free Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <a href="tel:4036044218">
                <Button 
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-6 text-lg font-semibold rounded-lg transition-all duration-300"
                >
                  <Phone className="mr-2 w-5 h-5" />
                  Call Now
                </Button>
              </a>
            </div>

            {/* Key Features */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl animate-in fade-in slide-in-from-bottom-4 duration-700 delay-400">
              <div className="flex items-center gap-3 text-white">
                <CheckCircle className="w-6 h-6 text-[#00AEEF] flex-shrink-0" />
                <span className="text-lg">Licensed & Insured</span>
              </div>
              <div className="flex items-center gap-3 text-white">
                <CheckCircle className="w-6 h-6 text-[#00AEEF] flex-shrink-0" />
                <span className="text-lg">15+ Years Experience</span>
              </div>
              <div className="flex items-center gap-3 text-white">
                <CheckCircle className="w-6 h-6 text-[#00AEEF] flex-shrink-0" />
                <span className="text-lg">100% Satisfaction</span>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto mb-12">
            {stats.map((stat) => (
              <Card key={stat.id} className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                <CardContent className="p-6 text-center">
                  <div className="text-4xl font-bold text-[#00AEEF] mb-2">{stat.value}</div>
                  <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center items-center gap-8 pt-8 border-t border-gray-200">
            <div className="flex items-center gap-3 text-gray-700">
              <div className="w-12 h-12 bg-[#00AEEF]/10 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-[#00AEEF]" />
              </div>
              <span className="font-semibold">Licensed & Insured</span>
            </div>
            <BBBSeal
              className="w-[300px] max-w-full rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300"
              testId="bbb-seal-home"
            />
            <div className="flex items-center gap-3 text-gray-700">
              <div className="w-12 h-12 bg-[#00AEEF]/10 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-[#00AEEF]" />
              </div>
              <span className="font-semibold">Since 2007</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">Our Expert Services</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive drywall and insulation solutions for all your needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-12">
            <Card className="border-none shadow-xl overflow-hidden group cursor-pointer hover:scale-105 transition-all duration-300">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1733431772808-82d878e59000?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxkcnl3YWxsJTIwY29uc3RydWN0aW9ufGVufDB8fHx8MTc3NDQ4Mjc2Mnww&ixlib=rb-4.1.0&q=85"
                  alt="Commercial & Residential Drywall"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="text-2xl font-bold mb-2">Drywall Installation</h3>
                  <p className="text-gray-200">Professional commercial & residential drywall services</p>
                </div>
              </div>
            </Card>

            <Card className="border-none shadow-xl overflow-hidden group cursor-pointer hover:scale-105 transition-all duration-300">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src="https://images.pexels.com/photos/6474207/pexels-photo-6474207.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Insulation Services"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="text-2xl font-bold mb-2">Insulation Solutions</h3>
                  <p className="text-gray-200">Spray foam, blow insulation & more</p>
                </div>
              </div>
            </Card>

            <Card className="border-none shadow-xl overflow-hidden group cursor-pointer hover:scale-105 transition-all duration-300">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_wall-works-1/artifacts/fqm7apho_IMG_6044.webp"
                  alt="Spray Foam Insulation"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="text-2xl font-bold mb-2">Spray Foam Insulation</h3>
                  <p className="text-gray-200">High-performance spray foam for maximum energy efficiency</p>
                </div>
              </div>
            </Card>
          </div>

          <div className="text-center">
            <Button 
              onClick={() => navigate('/services')}
              className="bg-[#00AEEF] hover:bg-[#0096d1] text-white px-8 py-6 text-lg font-semibold rounded-lg transition-all duration-300 hover:shadow-xl"
            >
              View All Services
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#00AEEF] to-[#0096d1] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">Ready to Start Your Project?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
            Get a free, no-obligation quote today and experience the Sat Drywall difference
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/contact')}
              className="bg-white text-[#00AEEF] hover:bg-gray-100 px-8 py-6 text-lg font-semibold rounded-lg transition-all duration-300 hover:shadow-xl"
            >
              Request a Quote
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              onClick={() => navigate('/about')}
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-[#00AEEF] px-8 py-6 text-lg font-semibold rounded-lg transition-all duration-300"
            >
              Learn More About Us
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
